import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { EXPERTISES_DATA } from "@/data/expertises";
import { EXPERTISES_EN } from "@/data/expertises-en";
import { useLang } from "@/i18n/LanguageContext";
import Stub from "@/pages/Stub";
import { usePageMeta } from "@/lib/usePageMeta";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

export default function ExpertiseDetail() {
    const { slug } = useParams();
    const { lang, t, prefix } = useLang();
    const DATA = lang === "en" ? EXPERTISES_EN : EXPERTISES_DATA;
    const expertise = DATA.find((e) => e.slug === slug);
    usePageMeta(
        expertise ? expertise.seoTitle || `${expertise.title} — WEYB` : "WEYB",
        expertise?.seoDescription || expertise?.tagline
    );

    if (!expertise) return <Stub title="Expertise introuvable" />;

    const idx = DATA.indexOf(expertise);
    const next = DATA[(idx + 1) % DATA.length];

    return (
        <>
            <script type="application/ld+json">{JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Accueil", item: "https://weyb.fr/" },
                    { "@type": "ListItem", position: 2, name: "Expertises", item: "https://weyb.fr/expertises" },
                    { "@type": "ListItem", position: 3, name: expertise.title },
                ],
            })}</script>
        <div data-testid={`expertise-page-${expertise.slug}`}>
            {/* Hero */}
            <section className="bg-white pt-32 md:pt-44 pb-20 md:pb-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <nav aria-label="Fil d'Ariane" className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-gray-400 mb-10">
                            <Link to={prefix || "/"} className="hover:text-weyb-yellow transition-colors" data-testid="breadcrumb-home">{t("expertiseDetail.breadcrumbHome")}</Link>
                            <span aria-hidden="true">/</span>
                            <Link to={`${prefix}/expertises`} className="hover:text-weyb-yellow transition-colors" data-testid="breadcrumb-expertises">{t("expertiseDetail.breadcrumbExpertises")}</Link>
                            <span aria-hidden="true">/</span>
                            <span className="text-weyb-yellow-dark">{expertise.title}</span>
                        </nav>
                    </Reveal>
                    <div className={`grid grid-cols-1 ${expertise.image ? "lg:grid-cols-12 gap-12 items-center" : ""}`}>
                    <div className={expertise.image ? "lg:col-span-7" : ""}>
                    <Eyebrow index={expertise.num}>{t("expertiseDetail.eyebrowLabel")}</Eyebrow>
                    <Reveal delay={0.08}>
                        <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05] max-w-4xl text-weyb-ink">
                            {expertise.h1Title || expertise.title}
                            <span className="block mt-3 text-xl md:text-2xl font-medium text-weyb-yellow-dark">{expertise.heroTagline || expertise.tagline}</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl text-gray-600">
                            {expertise.heroIntro || expertise.intro}
                        </p>
                    </Reveal>
                    <Reveal delay={0.24} className="mt-10 flex flex-wrap gap-4">
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="expertise-hero-cta-booking"
                            className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors"
                        >
                            {t("expertiseDetail.ctaBooking")}
                            <ArrowUpRight size={16} />
                        </a>
                        <Link
                            to={`${prefix}/contact`}
                            data-testid="expertise-hero-cta-contact"
                            className="inline-flex items-center gap-2 border border-gray-300 text-weyb-ink hover:border-weyb-yellow px-7 py-4 rounded-sm text-sm font-medium transition-colors"
                        >
                            {t("expertiseDetail.ctaContact")}
                        </Link>
                    </Reveal>
                    </div>
                    {expertise.image && (
                        <Reveal delay={0.2} className="lg:col-span-5 mt-12 lg:mt-0">
                            <div className="relative max-w-sm lg:max-w-none">
                                <span className="absolute -top-4 -left-4 w-14 h-14 border-t-2 border-l-2 border-weyb-yellow z-10" aria-hidden="true" />
                                <div className="overflow-hidden rounded-sm">
                                    <img
                                        src={expertise.image}
                                        alt={expertise.imageAlt}
                                        data-testid="expertise-hero-image"
                                        className={`w-full ${expertise.imageAspect || "aspect-[4/3]"} object-cover`}
                                    />
                                </div>
                            </div>
                        </Reveal>
                    )}
                    </div>
                </div>
            </section>

            {/* Ce que couvre l'expertise */}
            <section className="py-20 md:py-28 bg-weyb-ink text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow dark>{t("expertiseDetail.pointsEyebrow")}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-tight max-w-2xl">
                            {t("expertiseDetail.pointsTitle")}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {expertise.points.map((p, i) => (
                            <Reveal key={p.title} delay={(i % 3) * 0.06} className="h-full">
                                <div data-testid={`expertise-point-${i}`} className="h-full bg-weyb-ink hover:bg-white/5 p-8 transition-colors">
                                    <span className="font-display text-sm font-medium text-weyb-yellow">{String(i + 1).padStart(2, "0")}</span>
                                    <h3 className="mt-4 font-display text-lg font-medium">{p.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-white/55">{p.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Résultats */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <Eyebrow>{t("expertiseDetail.outcomesEyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">
                                {t("expertiseDetail.outcomesTitle")}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        <ul className="space-y-4">
                            {expertise.outcomes.map((o, i) => (
                                <Reveal key={o} delay={i * 0.05}>
                                    <li data-testid={`expertise-outcome-${i}`} className="flex items-start gap-4 border border-gray-200 rounded-sm p-5 hover:border-weyb-yellow transition-colors">
                                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center bg-weyb-yellow rounded-sm">
                                            <Check size={14} strokeWidth={3} className="text-weyb-ink" aria-hidden="true" />
                                        </span>
                                        <span className="text-base font-medium text-weyb-ink">{o}</span>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                        <Reveal delay={0.2} className="mt-10">
                            <Link
                                to={`${prefix}/solutions/${expertise.solution.slug}`}
                                data-testid="expertise-solution-link"
                                className="group inline-flex items-center gap-2 text-sm font-semibold text-weyb-ink hover:text-weyb-yellow-dark transition-colors"
                            >
                                {t("expertiseDetail.solutionLink").replace("{label}", expertise.solution.label)}
                                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                            </Link>
                            {(expertise.related || []).map((r, i) => (
                                <Link
                                    key={r.href}
                                    to={`${prefix}${r.href}`}
                                    data-testid={`expertise-related-link-${i}`}
                                    className="group mt-4 flex items-center gap-2 text-sm font-semibold text-weyb-ink hover:text-weyb-yellow-dark transition-colors"
                                >
                                    {r.label}
                                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                                </Link>
                            ))}
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* CTA + expertise suivante */}
            <section className="py-20 md:py-28 bg-[#F9FAFB] border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="bg-weyb-ink text-white rounded-sm p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
                            <div>
                                <h2 className="font-display text-2xl md:text-4xl font-light tracking-tight">
                                    {t("expertiseDetail.ctaTitle")}
                                </h2>
                                <p className="mt-3 text-sm text-white/60 max-w-md">
                                    {t("expertiseDetail.ctaText")}
                                </p>
                            </div>
                            <a
                                href={CAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="expertise-final-cta-booking"
                                className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors shrink-0"
                            >
                                {t("expertiseDetail.ctaButton")}
                                <ArrowUpRight size={16} />
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-10">
                        <Link
                            to={`${prefix}/expertises/${next.slug}`}
                            data-testid="expertise-next-link"
                            className="group flex items-center justify-between border border-gray-200 bg-white rounded-sm p-6 hover:border-weyb-yellow transition-colors"
                        >
                            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">{t("expertiseDetail.nextLabel")}</span>
                            <span className="flex items-center gap-3 font-display text-lg font-medium text-weyb-ink">
                                {next.title}
                                <ArrowRight size={17} className="text-weyb-yellow transition-transform group-hover:translate-x-1" />
                            </span>
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
        </>
    );
}
